import { useQuery } from '@tanstack/react-query';
import { scanApi } from './service';
import type { ScannableEntityType } from './types';

/**
 * Query keys for scan-resolution queries. The QR/label endpoints
 * stream binary content and don't go through React Query (the
 * frontend points <img>/<embed> straight at the service URL builders).
 *
 *   scanKeys.all                       every cached scan query
 *   scanKeys.resolve(type, id)         single resolution
 *   scanKeys.formats                   label format catalogue
 */
export const scanKeys = {
  all: ['scan'] as const,
  resolve: (entityType: ScannableEntityType, entityId: string) =>
    [...scanKeys.all, 'resolve', entityType, entityId] as const,
  formats: () => [...scanKeys.all, 'formats'] as const,
};

/**
 * Resolve a scanned (entityType, entityId) pair into a summary. The
 * scan page calls this once the camera detects a QR; the rest of the
 * UI rarely needs it directly.
 *
 * `staleTime` is generous (5 min) because a freshly-scanned entity
 * almost never changes between the scan and the navigation; we don't
 * want to refetch on tab focus right after the user lands on the
 * detail page.
 */
export function useScanResolve(
  entityType: ScannableEntityType | undefined,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey:
      entityType && entityId
        ? scanKeys.resolve(entityType, entityId)
        : scanKeys.all,
    queryFn: () =>
      scanApi.resolve(entityType as ScannableEntityType, entityId as string),
    enabled: Boolean(entityType && entityId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * List of supported thermal label formats. Cached aggressively — the
 * server enum is static and only changes with a deploy.
 */
export function useLabelFormats() {
  return useQuery({
    queryKey: scanKeys.formats(),
    queryFn: () => scanApi.listFormats(),
    staleTime: 60 * 60 * 1000,
  });
}
